/**
 * The seam between a masked text field and whatever kind of value it edits.
 *
 * The hard part of a field that reformats as you type is keeping the caret where the user left it
 * when the text around it changes length — the same problem for a currency amount and a D-U-N-S
 * number, so it lives here. What differs lives behind {@link FieldMask}.
 *
 * Every offset is a UTF-16 code unit, since that is what `String.slice` and `setSelectionRange`
 * count. A mask may render an astral literal, but not have astral significant characters.
 */

/**
 * Everything a masked field needs to know about one kind of value.
 *
 * One rule makes the caret arithmetic below possible: `render` may insert literals between
 * significant characters, but must neither add nor drop any. Grouping satisfies it; a date field
 * padding `2/3` into `02/03` does not, and needs different machinery than this. Only the count is
 * fixed — a numeric mask is free to render its canonical `.` as the locale's `,`.
 *
 * {@link assertMaskPreservesDraft} checks the rule in development.
 */
export type FieldMask<TValue> = {
  /** Field text to the characters that mean something, canonicalised. Unmask plus sanitize. */
  fromText: (text: string) => string
  /** A value arriving from outside — a parent write, or focus — to a draft. */
  fromValue: (value: TValue) => string
  /** Draft to what shows while typing. Must satisfy the rule above. */
  render: (draft: string) => string
  /** Value to its idle display, which may be richer than `render` (padded cents, say). */
  display: (value: TValue) => string
  /** The committed value, or `null` when the draft holds none. */
  parse: (draft: string) => TValue | null
  /** Whether a character survives `render` — what makes the caret arithmetic possible. */
  isSignificant: (char: string) => boolean
  inputMode?: 'numeric' | 'decimal' | 'tel'
}

type SignificancePredicate = (char: string) => boolean

/**
 * How many characters that survive a reformat precede `end`. Counting digits alone is not enough:
 * with the caret just after a freshly typed decimal separator it would land back in front of it.
 */
export function countSignificantBefore(
  text: string,
  end: number,
  isSignificant: SignificancePredicate,
): number {
  // split(''), not spread: spread walks code points, and the count has to agree with the code-unit
  // offsets caretAfterSignificant returns.
  return text.slice(0, Math.max(0, end)).split('').filter(isSignificant).length
}

/**
 * Offsets of the characters an edit can actually remove, so Backspace and Delete can step over the
 * separators and symbols the mask inserts on its own.
 */
export function significantPositions(text: string, isSignificant: SignificancePredicate): number[] {
  // split('') keeps `index` a string offset, which is what slice() and setSelectionRange() take.
  // Spreading would number code points, and an astral literal would land an edit mid-character.
  return text.split('').flatMap((char, index) => (isSignificant(char) ? [index] : []))
}

/**
 * Where the caret goes so that `count` surviving characters sit behind it. Character offsets are
 * meaningless across a reformat; how much of the value is behind the caret is not.
 */
export function caretAfterSignificant(
  masked: string,
  count: number,
  isSignificant: SignificancePredicate,
): number {
  const positions = significantPositions(masked, isSignificant)
  // Before the first surviving character when nothing is behind the caret yet, else just past the
  // one it counted up to.
  const target = count === 0 ? positions[0] : positions[count - 1]
  if (target === undefined) return masked.length
  return count === 0 ? target : target + 1
}

/**
 * Complain in development when a mask breaks the rule {@link FieldMask} documents.
 *
 * Worth checking rather than only documenting: breaking it does not fail loudly, it produces a
 * caret that drifts on some keystrokes and not others, which is miserable to debug from outside.
 */
export function assertMaskPreservesDraft<TValue>(mask: FieldMask<TValue>, draft: string): void {
  if (process.env.NODE_ENV === 'production') return
  const rendered = mask.render(draft)
  const survived = rendered.split('').filter(mask.isSignificant).length
  const expected = draft.split('').filter(mask.isSignificant).length
  if (survived !== expected) {
    console.error(
      `[MaskedField] render() must keep the number of significant characters in the draft.\n` +
        `  draft:  ${JSON.stringify(draft)} (${expected} significant)\n` +
        `  render: ${JSON.stringify(rendered)} (${survived} significant)\n` +
        `The caret cannot be placed reliably while this holds.`,
    )
  }
}
